import React, { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE } from "@/lib/api";
import { Trophy, Radio } from "lucide-react";

/**
 * /tv/scoreboard — Public full-screen TV scoreboard.
 *
 * • No login required.
 * • Shows up to 6 matches at once in a responsive grid (2×3 desktop, 1-col mobile).
 * • Groups matches by tie so each card renders all 3 seed rows with:
 *     - Seed number + court
 *     - Player names for each team's pair
 *     - Live padel game score (0 / 15 / 30 / 40 / AD / DEUCE) + games-in-set totals
 *     - Match status (LIVE / SCHEDULED / FINISHED)
 * • Prioritises LIVE ties first, then upcoming, then finished — fills any empty
 *   slots with the next scheduled ties.
 * • Polls the same public endpoint the Broadcast Studio uses (/api/public/matches)
 *   every 3 seconds — the referee-input path writes to `match.tennis_state` and
 *   the endpoint reads from it, so both flows share one source of truth.
 */
const POLL_MS = 3000;
const MAX_TIES = 6;

const bucket = (s) =>
  s === "live" ? 0 : s === "scheduled" ? 1 : s === "finished" ? 2 : 3;

export default function TVScoreboard() {
  const [matches, setMatches] = useState([]);
  const [connected, setConnected] = useState(false);
  const [tick, setTick] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await fetch(`${API_BASE}/public/matches`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) {
          setMatches(Array.isArray(data) ? data : []);
          setConnected(true);
          setTick((t) => t + 1);
        }
      } catch (e) {
        if (!cancelled) setConnected(false);
      }
    };
    fetchOnce();
    timerRef.current = setInterval(fetchOnce, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
    };
  }, []);

  const tieCards = useMemo(() => {
    const byTie = new Map();
    for (const m of matches) {
      const key = m.tie_id || `solo:${m.id}`;
      if (!byTie.has(key)) {
        byTie.set(key, {
          tie_id: m.tie_id,
          team_a_id: m.team_a_id,
          team_b_id: m.team_b_id,
          team_a_name: m.team_a_name,
          team_b_name: m.team_b_name,
          stage: m.stage,
          court: m.court,
          scheduled_at: m.scheduled_at,
          statuses: new Set(),
          matches: [],
        });
      }
      const t = byTie.get(key);
      t.matches.push(m);
      t.statuses.add((m.status || "scheduled").toLowerCase());
    }
    const cards = Array.from(byTie.values()).map((t) => ({
      ...t,
      status: t.statuses.has("live")
        ? "live"
        : Array.from(t.statuses).every((s) => s === "finished")
        ? "finished"
        : "scheduled",
      matches: t.matches.sort((a, b) => (a.seed || 99) - (b.seed || 99)),
    }));
    cards.sort((a, b) => {
      const db = bucket(a.status) - bucket(b.status);
      if (db) return db;
      return (a.scheduled_at || "").localeCompare(b.scheduled_at || "");
    });
    return cards.slice(0, MAX_TIES);
  }, [matches]);

  return (
    <div
      className="min-h-screen bg-[#050d09] text-white overflow-hidden"
      data-testid="tv-scoreboard-page"
    >
      <div className="kenya-stripe" />
      <div className="px-6 py-3 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-3">
          <Trophy className="w-6 h-6 text-[#F4B400]" />
          <div>
            <div className="font-anton text-2xl md:text-3xl uppercase leading-none">
              Mara Padel League — Live
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              TV Scoreboard · updates every {POLL_MS / 1000}s
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={`w-2 h-2 rounded-full ${
              connected ? "bg-red-500 animate-pulse" : "bg-zinc-600"
            }`}
            data-testid="tv-scoreboard-live-dot"
          />
          <span className="text-[10px] uppercase tracking-widest text-zinc-400">
            {connected ? "LIVE" : "reconnecting…"}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-600 tabular-nums ml-2">
            tick {tick}
          </span>
        </div>
      </div>

      <div
        className="grid gap-3 p-3 md:p-4"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 480px), 1fr))" }}
        data-testid="tv-scoreboard-grid"
      >
        {tieCards.length === 0 && (
          <div className="col-span-full p-12 text-center text-zinc-500 italic">
            No matches scheduled.
          </div>
        )}
        {tieCards.map((t) => (
          <TieCard key={t.tie_id || t.matches[0]?.id} tie={t} />
        ))}
      </div>
      <div className="kenya-stripe" />
    </div>
  );
}

function TieCard({ tie }) {
  const statusPill =
    tie.status === "live"
      ? "bg-red-500/25 text-red-200 border-red-500/50 animate-pulse"
      : tie.status === "finished"
      ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/40"
      : "bg-white/10 text-zinc-200 border-white/10";
  return (
    <div
      className="rounded-xl border border-white/10 bg-[#0f1a15] p-4 flex flex-col"
      data-testid={`tv-tie-card-${tie.tie_id || tie.matches[0]?.id}`}
    >
      <div className="flex items-start justify-between mb-3 gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
            {(tie.stage || "").toUpperCase()}
            {tie.court && ` · ${tie.court}`}
          </div>
          <div className="font-anton text-2xl md:text-3xl uppercase tracking-tight leading-tight text-white truncate">
            {tie.team_a_name} <span className="text-zinc-500">vs</span> {tie.team_b_name}
          </div>
        </div>
        <span
          className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded border shrink-0 ${statusPill}`}
          data-testid={`tv-tie-status-${tie.tie_id || tie.matches[0]?.id}`}
        >
          {tie.status === "live" ? "🔴 LIVE" : tie.status.toUpperCase()}
        </span>
      </div>
      <div className="space-y-1.5 flex-1">
        {tie.matches.map((m) => (
          <SeedMatchRow key={m.id} match={m} />
        ))}
        {tie.matches.length === 0 && (
          <div className="text-xs italic text-zinc-500 p-4 text-center">
            No matches in this tie.
          </div>
        )}
      </div>
    </div>
  );
}

function SeedMatchRow({ match }) {
  const seedNum = match.seed;
  const accent =
    seedNum === 1 ? "#F4B400" : seedNum === 2 ? "#00A86B" : seedNum === 3 ? "#7DD3FC" : "#94a3b8";
  const status = (match.status || "scheduled").toLowerCase();
  const isLive = status === "live";
  const isFinished = status === "finished";
  const playersA = match.players_a?.length
    ? match.players_a.join(" + ")
    : match.team_a_name || "TBD";
  const playersB = match.players_b?.length
    ? match.players_b.join(" + ")
    : match.team_b_name || "TBD";
  const gamesA = match.total_games_won_a ?? 0;
  const gamesB = match.total_games_won_b ?? 0;
  const disp = match.tennis_display;
  const showGameScore = isLive && disp;
  return (
    <div
      className="rounded-lg border border-white/5 bg-black/30 p-2"
      style={{ borderLeft: `3px solid ${accent}` }}
      data-testid={`tv-seed-row-${match.id}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span
          className="font-anton text-sm uppercase tracking-tight leading-none"
          style={{ color: accent }}
        >
          Seed {seedNum || "?"}
        </span>
        <span
          className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
            isLive
              ? "bg-red-500/20 text-red-300 animate-pulse"
              : isFinished
              ? "bg-emerald-500/20 text-emerald-300"
              : "bg-white/5 text-zinc-500"
          }`}
        >
          {isLive ? "LIVE" : status}
        </span>
        {match.court && !isLive && (
          <span className="text-[9px] text-zinc-600">· {match.court}</span>
        )}
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className="text-right min-w-0">
          <div className="text-xs md:text-sm text-white truncate leading-tight">
            {playersA}
          </div>
        </div>
        <div className="flex flex-col items-center min-w-[70px]">
          {showGameScore && disp.label !== "DEUCE" && disp.label !== "ADVANTAGE A" && disp.label !== "ADVANTAGE B" && (
            <div
              className="font-anton text-2xl tabular-nums leading-none"
              data-testid={`tv-seed-live-${match.id}`}
            >
              <span style={{ color: accent }}>{disp.a}</span>
              <span className="text-zinc-600 mx-1">·</span>
              <span style={{ color: accent }}>{disp.b}</span>
            </div>
          )}
          {showGameScore && (disp.label === "DEUCE" || disp.label === "ADVANTAGE A" || disp.label === "ADVANTAGE B") && (
            <div
              className="font-anton text-lg leading-none"
              style={{ color: accent }}
              data-testid={`tv-seed-live-${match.id}`}
            >
              {disp.label === "DEUCE" ? "DEUCE" : disp.label === "ADVANTAGE A" ? "AD-40" : "40-AD"}
            </div>
          )}
          <div className="font-anton text-xs tabular-nums text-zinc-400 mt-0.5">
            <span className="text-white">{gamesA}</span>
            <span className="text-zinc-600 mx-1">—</span>
            <span className="text-white">{gamesB}</span>
          </div>
          {(match.set_scores || []).length > 1 && (
            <div className="text-[9px] text-zinc-500 tabular-nums leading-none mt-0.5">
              {(match.set_scores || [])
                .map((s) => `${s.a}-${s.b}`)
                .join(" | ")}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <div className="text-xs md:text-sm text-white truncate leading-tight">
            {playersB}
          </div>
        </div>
      </div>
    </div>
  );
}
